<template>
  <div
    class="listing-row position-relative"
    :class="status"
    @click="toggleBasket"
  >
    <!-- Content -->
    <div class="listing-content">
      <div
        v-if="status === 'unpaid' || status === 'paid' || status === 'unpaidCancelled' || status === 'paidCancelled'"
        style="font-size: 10px; font-weight: 500; top: -5px"
        class="text-primary position-relative"
      >
        <i class="fas fa-check" />
        Zamówione
      </div>
      <div class="listing-header">
        <div
          class="listing-name"
          :style="{
            'text-decoration': status === 'unpaidCancelled' || status === 'paidCancelled' ? 'line-through' : 'none' }"
        >
          <i
            v-if="status === 'inBasket'"
            class="fas fa-cart-shopping text-primary"
          />
          {{ listing.name }}
        </div>
        <div class="listing-price">
          <Money
            :value="listing.price"
            :currency="listing.currency"
          />
        </div>
      </div>

      <div
        v-if="listing.description"
        class="listing-description"
      >
        {{ listing.description }}
      </div>

      <!-- Products -->
      <div
        v-if="details && products"
        class="listing-products"
      >
        <div
          v-for="productId in listing.productIds"
          :key="productId"
          class="product-item"
        >
          <span class="product-name">
            {{ getProductName(productId) }}
          </span>
        </div>
      </div>

      <!-- Menu items -->
      <div
        v-if="listing.menu && listing.menu.length > 0"
        class="listing-menu"
      >
        <div
          v-for="menuItem in listing.menu"
          :key="menuItem.id"
          class="menu-item"
        >
          <span class="menu-item-name">- {{ menuItem.title }}</span>
          <span
            v-if="menuItem.tags"
            class="menu-item-description"
          >
            {{ menuItem.tags.join(', ') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Money from '@/components/Money';
import { mapGetters } from 'vuex';

export default {
  name: 'ListingRow',
  props: {
    listing: {
      type: Object,
      required: true,
    },
    payerId: {
      type: String,
      required: true,
    },
    details: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: 'available',
    },
    products: {
      type: Array,
      default: () => [],
    },
    catalog: Object,
    day: String,
  },
  components: {
    Money,
  },
  computed: {
    ...mapGetters([
      'catalogBasket',
    ]),
    basketEntry() {
      return this.catalogBasket
        .find((x) => x.payerId === this.payerId
        && x.offerId === this.listing.id
        && x.day === this.day);
    },
    ordered() {
      return this.status === 'paid' || this.status === 'unpaid';
    },
    statusClass() {
      const statusMap = {
        available: 'status-available',
        inBasket: 'status-in-basket',
        paid: 'status-paid',
        unpaid: 'status-unpaid',
        paidCancelled: 'status-paid-cancelled',
        unpaidCancelled: 'status-unpaid-cancelled',
      };
      return statusMap[this.status] || 'status-available';
    },
  },
  methods: {
    getProductName(productId) {
      const product = this.products.find((p) => p.id === productId);
      return product?.name || productId;
    },
    toggleBasket() {
      if (this.basketEntry) {
        this.removeFromBasket();
      } else {
        this.addToBasket();
      }
    },
    removeFromBasket() {
      this.$store.commit('removeFromBasket', {
        payerId: this.payerId,
        offerId: this.listing.id,
        isReturned: !!this.ordered,
        day: this.day,
      });

      this.$emit('close');
    },
    addToBasket() {
      this.$store.commit('addBasketCatalog', {
        payerId: this.payerId,
        price: this.listing.price,
        value: this.listing.price,
        currency: this.listing.currency,
        payeeId: this.catalog.catalog.payeeId,
        catalogId: this.catalog.catalog.id,
        offerName: this.listing.name,
        offerId: this.listing.id,
        catalog: this.catalog.catalog,
        listing: this.listing,
        isReturned: !!this.ordered,
        day: this.day,
        status: 'inBasket',
      });

      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.listing-row {
  display: flex;
  align-items: flex-start;
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-bottom: 10px;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
  }
}

.status-indicator {
  margin-right: 15px;
  margin-top: 2px;
}

.listing-row {
  &.unpaid,
  &.paid,
  &.unpaidCancelled,
  &.paidCancelled {
    background-color: #cce3ff;
  }
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;

  &.status-available {
    background-color: #28a745;
  }

  &.status-in-basket {
    background-color: #007bff;
  }

  &.status-paid {
    background-color: #28a745;
  }

  &.status-unpaid {
    background-color: #ffc107;
  }

  &.status-paid-cancelled {
    background-color: #dc3545;
  }

  &.status-unpaid-cancelled {
    background-color: #6c757d;
  }
}

.listing-content {
  flex-grow: 1;
}

.listing-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.listing-name {
  font-weight: 500;
  font-size: 16px;
  color: #333;
  flex-grow: 1;
  margin-right: 15px;
}

.listing-price {
  font-weight: 500;
  font-size: 16px;
  color: #007bff;
  white-space: nowrap;
}

.listing-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
  line-height: 1.4;
}

.product-item {
  background-color: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
  display: inline-block;
  margin-right: 8px;

  .product-name {
    font-size: 12px;
    color: #495057;
  }
}

.listing-menu {
  margin-bottom: 10px;
}

.menu-item {
  margin-bottom: 2px;
  margin-left: 10px;
  line-height: 1.2;

  .menu-item-name {
    // font-weight: 500;
    font-size: 12px;
    color: #333;
  }

  .menu-item-description {
    font-size: 10px;
    color: #666;
    margin-left: 8px;
  }
}

.listing-times {
  border-top: 1px solid #e0e0e0;
  padding-top: 10px;
}

.time-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;

  .time-label {
    font-size: 12px;
    color: #666;
  }

  .time-value {
    font-size: 12px;
    color: #333;
    font-weight: 500;
  }
}
</style>
